import { Card } from "@/components/ui/card";
import { CreditCard, ArrowUpRight, Clock, TrendingUp, Users } from "lucide-react";
import { useCurrency } from "@/hooks/use-currency";

interface SubscriptionStatsProps {
  totalRevenue: number;
  completedSubscriptions: number;
  pendingSubscriptions: number;
}

export const SubscriptionStats = ({
  totalRevenue,
  completedSubscriptions,
  pendingSubscriptions,
}: SubscriptionStatsProps) => {
  const { formatAmount, currency } = useCurrency();
  
  return (
    <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
      <Card className="p-6 glass-card hover:shadow-xl transition-all duration-300 group animate-fade-in">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">Total Revenue</p>
            <p className="text-2xl font-bold text-primary">
              {formatAmount(totalRevenue, currency)}
            </p>
          </div>
          <div className="p-3 bg-primary/10 rounded-xl transition-transform duration-300 group-hover:scale-110">
            <CreditCard className="h-5 w-5 text-primary" />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">From all sources</span>
          <span className="flex items-center text-green-600">
            <TrendingUp className="h-4 w-4 mr-1" />
            12.5%
          </span>
        </div>
      </Card>

      <Card className="p-6 glass-card hover:shadow-xl transition-all duration-300 group animate-fade-in delay-100">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">Completed Payments</p>
            <p className="text-2xl font-bold text-green-600">{completedSubscriptions}</p>
          </div>
          <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl transition-transform duration-300 group-hover:scale-110">
            <Users className="h-5 w-5 text-green-600" />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">All paid transactions</span>
          <span className="flex items-center text-green-600">
            <ArrowUpRight className="h-4 w-4 mr-1" />
            68%
          </span>
        </div>
      </Card>

      <Card className="p-6 glass-card hover:shadow-xl transition-all duration-300 group animate-fade-in delay-200">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">Pending Payments</p>
            <p className="text-2xl font-bold text-yellow-600">{pendingSubscriptions}</p>
          </div>
          <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl transition-transform duration-300 group-hover:scale-110">
            <Clock className="h-5 w-5 text-yellow-600" />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Awaiting payment</span>
          <span className="flex items-center text-yellow-600">
            <Clock className="h-4 w-4 mr-1" />
            2.4h avg
          </span>
        </div>
      </Card>

      <Card className="p-6 glass-card hover:shadow-xl transition-all duration-300 group animate-fade-in delay-300">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">Growth Rate</p>
            <p className="text-2xl font-bold text-blue-600">+24.5%</p>
          </div>
          <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl transition-transform duration-300 group-hover:scale-110">
            <TrendingUp className="h-5 w-5 text-blue-600" />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Monthly target</span>
          <span className="flex items-center text-blue-600">
            <TrendingUp className="h-4 w-4 mr-1" />
            86%
          </span>
        </div>
      </Card>
    </div>
  );
};